// @flow

import React from 'react';
import { Link, withRouter, type ContextRouter } from 'react-router-dom';
import { compose, withState } from 'recompose';
import fetch from 'node-fetch';
import {
  format,
  parse // eslint-disable-line node/no-deprecated-api
} from 'url';
import querystring from 'querystring';
import { FormattedMessage, defineMessages, injectIntl } from 'react-intl';
import { CYPRESS, AUTH_EMAIL } from '../const/path';

import {
  BaseText,
  Input,
  Line,
  PasswordInput,
  TextInput,
  WebformView,
  injectSheet,
  type InjectSheetProvidedProps,
  type ThemeType
} from '@catalytic/catalytic-ui';

import { fieldValueForKey } from '../Field/FieldHelpers';

import LoginButton from '../shared/Button/LoginButton';
import LoginHeadline from './LoginHeadline';
import LoginHelp from './LoginHelp';
import LoginLayout from './LoginLayout';
import withTeam, { type WithTeamProps } from './withTeam';

export const getReferrer = (defaultRedirect: string = '/') => {
  const { referrer } = querystring.parse(
    parse(window.location.href || '/').query || ''
  );
  const { hash, host, pathname, search } = parse(
    referrer || document.referrer || '/',
    false,
    true
  );

  if (
    // If the referrer host is different
    (host && host !== window.location.host) ||
    // or the referrer pathname is the same
    pathname === window.location.pathname ||
    // or the referrer is a Cypress URL
    pathname === CYPRESS
  ) {
    // then redirect to the default
    return defaultRedirect;
  }

  // Convert referrer to a relative URL
  return format({ hash, pathname, search });
};

const EMAIL_KEY = 'email';
const PASSWORD_KEY = 'password';

export const Messages = defineMessages({
  emailPlaceholder: {
    id: 'login.findTeamsEmail',
    defaultMessage: 'email address'
  },
  loginErrorMessage: {
    id: 'login.invalid',
    defaultMessage: 'Invalid email/password'
  },
  passwordPlaceholder: {
    id: 'login.findTeamsPassword',
    defaultMessage: 'password'
  }
});

export const Style = (theme: ThemeType) => ({
  container: {
    margin: 'auto',
    padding: '1rem',
    width: '100vw',
    maxWidth: '100%',
    minHeight: '1rem',
    [theme.breakpoints.gt.mobile.portrait]: {
      width: '24rem'
    }
  },
  divider: {
    backgroundColor: theme.colors.lightGrey,
    height: '1px',
    marginTop: '2rem',
    marginBottom: '2rem',
    width: '37%'
  },
  primaryInput: {
    marginBottom: '0.75rem'
  },
  input: {
    width: '100%',
    margin: 'auto'
  },
  or: {
    color: theme.colors.battleshipGrey,
    textTransform: 'uppercase'
  },
  orContainer: {
    width: '100%',
    display: 'flex',
    flexFlow: 'row wrap',
    alignItems: 'center',
    justifyContent: 'space-between'
  }
});

type Props = ContextRouter &
  InjectSheetProvidedProps &
  WithTeamProps & {
    error: boolean,
    intl: Object,
    setEmailState(email: string): void,
    setErrorState(error: boolean): void,
    setPasswordState(password: string): void
  };

export function Login(props: Props) {
  const {
    classes,
    error,
    setEmailState,
    setPasswordState,
    setErrorState,
    fetched,
    intl,
    teamDisplayName
  } = props;

  const handleSubmit = (
    event: SyntheticEvent<HTMLFormElement>,
    formData: FormData
  ) => {
    event.preventDefault();
    const email = fieldValueForKey(formData, EMAIL_KEY);
    const password = fieldValueForKey(formData, PASSWORD_KEY);
    const referrer = getReferrer();

    return fetch(`/api/auth`, {
      headers: { 'Content-type': 'application/json' },
      method: 'POST',
      credentials: 'same-origin',
      body: JSON.stringify({
        username: email.toLowerCase(),
        password
      })
    })
      .then(res => {
        if (res.status === 200) {
          window.location = referrer;
        } else {
          setErrorState(true);
        }
      })
      .catch(e => {
        console.error(intl.formatMessage(Messages.loginErrorMessage), e);
      });
  };
  // https://github.com/facebook/flow/issues/4303
  // $FlowIgnore
  const emailRef = React.useCallback(element => element?.focus(), []);

  return (
    <LoginLayout fetched={fetched}>
      <LoginHeadline
        headlineText={
          <FormattedMessage
            id="login.headline"
            defaultMessage="Sign in to {teamDisplayName}"
            values={{ teamDisplayName }}
          />
        }
      />
      <div className={classes.container}>
        <WebformView
          hasSubmit={false}
          implicitSubmission
          onSubmit={handleSubmit}
        >
          <Input
            className={classes.primaryInput}
            inputClassName={classes.input}
            error={error ? intl.formatMessage(Messages.loginErrorMessage) : ''}
          >
            <TextInput
              hasError={error}
              hasRequired={false}
              id={EMAIL_KEY}
              inputRef={emailRef}
              name={EMAIL_KEY}
              onChange={event => {
                if (error) {
                  setErrorState(false);
                }
                setEmailState(event.currentTarget);
              }}
              placeholder={intl.formatMessage(Messages.emailPlaceholder)}
              required
              type="text"
            />
          </Input>
          <Input
            className={classes.primaryInput}
            inputClassName={classes.input}
          >
            <PasswordInput
              autoComplete="on"
              hasError={error}
              hasRequired={false}
              id={PASSWORD_KEY}
              name={PASSWORD_KEY}
              onChange={e => {
                if (error) {
                  setErrorState(false);
                }
                setPasswordState(e.target.value);
              }}
              placeholder={intl.formatMessage(Messages.passwordPlaceholder)}
              required
              type="password"
            />
          </Input>
          <LoginButton
            type="submit"
            value="submit"
            data-testid="sign-in-button"
          >
            <FormattedMessage
              id="login.signInButton"
              defaultMessage="Sign in"
            />
          </LoginButton>
        </WebformView>
        <div className={classes.orContainer}>
          <Line className={classes.divider} />
          <BaseText className={classes.or}>
            <FormattedMessage id="login.or" defaultMessage="or" />
          </BaseText>
          <Line className={classes.divider} />
        </div>
        <Link to={AUTH_EMAIL}>
          <LoginButton data-testid="sign-in-by-email-button">
            <FormattedMessage
              id="login.signInEmailButton"
              type="submit"
              defaultMessage="Sign in by email"
            />
          </LoginButton>
        </Link>
      </div>
      <LoginHelp />
    </LoginLayout>
  );
}
export default compose(
  withTeam,
  withRouter,
  withState('email', 'setEmailState', ({ email }) => {
    return email;
  }),
  withState('error', 'setErrorState', ({ error }) => {
    return error;
  }),
  withState('password', 'setPasswordState', ({ password }) => {
    return password;
  }),
  injectIntl,
  injectSheet(Style)
)(Login);
